import { useState, useEffect, useCallback } from 'react';
import { fetchUserInteractions, recordSocialShare, incrementShareCount } from '../lib/api/interactions';
import { useAuth } from '../contexts/AuthContext';
import type { InteractionMessage } from '../types/interactions';

// Sample data for testing when no real interactions exist
const SAMPLE_INTERACTIONS: InteractionMessage[] = [
  {
    id: 'sample-1',
    user_id: 'sample-user',
    message_type: 'morning_prompt',
    message_content: 'Good morning! ☀️ What\'s the one thing you want to accomplish today?',
    sent_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    response_received: true,
    response_content: 'I want to finish the quarterly report and send it to my manager.',
    response_received_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
    resource_url: null,
    agent_id: 'onewin-ai',
    role: 'assistant',
    has_image: true,
    image_url: 'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=800',
    share_count: 3
  },
  {
    id: 'sample-2',
    user_id: 'sample-user',
    message_type: 'evening_checkin',
    message_content: 'How did your goal go today? Did you complete your quarterly report?',
    sent_at: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(), // 18 hours ago
    response_received: true,
    response_content: 'Yes! I finished it and got great feedback from my manager. Feeling accomplished! 🎉',
    response_received_at: new Date(Date.now() - 17.5 * 60 * 60 * 1000).toISOString(),
    resource_url: null,
    agent_id: 'onewin-ai',
    role: 'assistant',
    has_image: false,
    image_url: null,
    share_count: 1
  },
  {
    id: 'sample-3',
    user_id: 'sample-user',
    message_type: 'follow_up',
    message_content: 'Fantastic work on completing your quarterly report! 🎯 Your consistency is building real momentum. Ready for tomorrow\'s challenge?',
    sent_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
    response_received: false,
    response_content: null,
    response_received_at: null,
    resource_url: null,
    agent_id: 'onewin-ai',
    role: 'assistant',
    has_image: true,
    image_url: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800',
    share_count: 5
  },
  {
    id: 'sample-4',
    user_id: 'sample-user',
    message_type: 'audio',
    message_content: 'I recorded a voice note about my workout goal for today.',
    sent_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    response_received: true,
    response_content: 'Thanks for the voice note! I heard your commitment to the 30-minute workout. You\'ve got this!',
    response_received_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000).toISOString(),
    resource_url: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav',
    agent_id: 'onewin-ai',
    role: 'user',
    has_image: false,
    image_url: null,
    share_count: 0
  }
];

export function useInteractions(limit: number = 50) {
  const { user } = useAuth();
  const [interactions, setInteractions] = useState<InteractionMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [usingSampleData, setUsingSampleData] = useState(false);

  const fetchInteractions = useCallback(async (reset: boolean = false) => {
    if (!user) return;

    try {
      setLoading(true);
      const currentOffset = reset ? 0 : offset;
      
      const { data, error: fetchError } = await fetchUserInteractions(
        user.id,
        limit,
        currentOffset
      );

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        // Real data exists
        setUsingSampleData(false);
        if (reset) {
          setInteractions(data);
          setOffset(data.length);
        } else {
          setInteractions(prev => [...prev, ...data]);
          setOffset(prev => prev + data.length);
        }
        setHasMore(data.length === limit);
      } else if (reset && currentOffset === 0) {
        // No real data, use sample data for testing
        setUsingSampleData(true);
        setInteractions(SAMPLE_INTERACTIONS);
        setOffset(SAMPLE_INTERACTIONS.length);
        setHasMore(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch interactions');
      // Fallback to sample data on error for testing
      if (reset) {
        setUsingSampleData(true);
        setInteractions(SAMPLE_INTERACTIONS);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  }, [user, limit, offset]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && !usingSampleData) {
      fetchInteractions(false);
    }
  }, [loading, hasMore, usingSampleData, fetchInteractions]);

  const refresh = useCallback(() => {
    setOffset(0);
    setHasMore(true);
    setError(null);
    fetchInteractions(true);
  }, [fetchInteractions]);

  const shareMessage = useCallback(async (messageId: string, platform: string) => {
    if (!user) return { success: false, error: 'User not authenticated' };

    try {
      if (usingSampleData) {
        // Simulate successful share for sample data
        setInteractions(prev => 
          prev.map(interaction => 
            interaction.id === messageId 
              ? { ...interaction, share_count: interaction.share_count + 1 }
              : interaction
          )
        );
        return { success: true, error: null };
      }

      // Real share logic
      const { error: shareError } = await recordSocialShare(user.id, messageId, platform);
      if (shareError) throw shareError;

      const { error: incrementError } = await incrementShareCount(messageId);
      if (incrementError) throw incrementError;

      setInteractions(prev => 
        prev.map(interaction => 
          interaction.id === messageId 
            ? { ...interaction, share_count: interaction.share_count + 1 }
            : interaction
        )
      );

      return { success: true, error: null };
    } catch (err) {
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Failed to record share' 
      };
    }
  }, [user, usingSampleData]);

  useEffect(() => {
    if (user) {
      fetchInteractions(true);
    }
  }, [user]);

  return {
    interactions,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    shareMessage,
    usingSampleData
  };
}